import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Pagination,
  InputGroup,
  Card,
  CardBody
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Promocode } from "./types";
import CreatePromocodeOffcanvas from "./CreatePromocodeOffcanvas";
import { MdAdd } from "react-icons/md";
import Swal from 'sweetalert2';
import Loader from "../../components/Loader";
import axios from "axios";
import AppConstants from "../../AppConstants";
import './index.scss';

const PromocodeManagement: React.FC = () => {
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);
  const [filteredPromocodes, setFilteredPromocodes] = useState<Promocode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [totalPromocodes, setTotalPromocodes] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  // Offcanvas states
  const [showCreateOffcanvas, setShowCreateOffcanvas] = useState<boolean>(false);
  const [showEditOffcanvas, setShowEditOffcanvas] = useState<boolean>(false);
  const [selectedPromocode, setSelectedPromocode] = useState<Promocode | null>(null);
  const [editLoading, setEditLoading] = useState<boolean>(false);


  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);

  const handleSearch = (query: string) => {
    setSearchTerm(query);

    // Clear existing debounce
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    // Set new debounce
    const newDebounce = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching

      // Determine active filter value
      let activeFilter: boolean | undefined;
      if (statusFilter === 'active') {
        activeFilter = true;
      } else if (statusFilter === 'inactive') {
        activeFilter = false;
      }

      fetchPromocodes(1, itemsPerPage, query, activeFilter);
    }, 500); // 500ms debounce delay

    setSearchDebounce(newDebounce);
  };

  // Update the existing useEffect for initial load
  useEffect(() => {
    // Determine active filter value
    let activeFilter: boolean | undefined;
    if (statusFilter === 'active') {
      activeFilter = true;
    } else if (statusFilter === 'inactive') {
      activeFilter = false;
    }

    fetchPromocodes(currentPage, itemsPerPage, searchTerm, activeFilter);
  }, [currentPage, itemsPerPage]);

  // Update the status filter useEffect
  useEffect(() => {
    // Reset to first page when filter changes
    setCurrentPage(1);

    // Determine active filter value
    let activeFilter: boolean | undefined;
    if (statusFilter === 'active') {
      activeFilter = true;
    } else if (statusFilter === 'inactive') {
      activeFilter = false;
    }

    fetchPromocodes(1, itemsPerPage, searchTerm, activeFilter);
  }, [statusFilter]);

  // API function to fetch all promocodes
  const fetchPromocodes = async (page: number = 1, limit: number = 10, searchQuery?: string, activeFilter?: boolean) => {
    try {
      setLoading(true);

      // Determine which API endpoint to use
      const isSearching = searchQuery && searchQuery.trim() !== '';
      const baseUrl = isSearching
        ? `${AppConstants.ADMIN_GET_PROMOCODES_API}/search`
        : AppConstants.ADMIN_GET_PROMOCODES_API;

      // Build query parameters
      const params: any = {
        page: page,
        limit: limit,
        sort: 'createdAt',
        order: 'desc'
      };

      // Add search-specific parameters
      if (isSearching) {
        params.query = searchQuery.trim();
      }

      // Add status filter if specified
      if (activeFilter !== undefined) {
        params.isActive = activeFilter;
      }

      const response = await axios.get(baseUrl, {
        params,
        headers: {
          "Content-Type": "application/json",
        }
      });

      // console.log('API URL:', `${baseUrl}?${new URLSearchParams(params).toString()}`);
      // console.log('Full API Response:', response.data);

      if (response.status === 200) {
        // Transform API response to match frontend Promocode type
        const transformedPromocodes: Promocode[] = response.data.promocodes?.map((item: any) => ({
          id: item._id || item.id,
          code: item.code,
          discount: item.discount,
          validUntil: item.validUntil ? new Date(item.validUntil).toLocaleDateString() : 'No expiry',
          maxRedemptions: item.maxRedemptions || 'Unlimited',
          // usageCount: item.usageCount || 0,
          usageCount: item.timesRedeemed || 0,
          isActive: item.isActive
        })) || [];

        setPromocodes(transformedPromocodes);
        setFilteredPromocodes(transformedPromocodes);

        // Set pagination data from API response
        setTotalPromocodes(response.data.pagination?.total || transformedPromocodes.length);
        setTotalPages(response.data.pagination?.totalPages || Math.ceil(transformedPromocodes.length / limit));
      }
    } catch (error: any) {
      console.error("Error fetching promocodes:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load promocodes. Please try again.";

      toast.error(message, {
        position: "top-right"
      });
    } finally {
      setLoading(false);
    }
  };

  // API function to fetch single promocode for editing
  const fetchSinglePromocode = async (promocodeId: string) => {
    try {
      setEditLoading(true);

      const response = await axios.get(
        `${AppConstants.ADMIN_GET_SINGLE_PROMOCODE_API}/${promocodeId}`,
        {
          headers: {
            "Content-Type": "application/json",

          }
        }
      );

      // console.log('Single Promocode Response:', response.data);

      if (response.status === 200) {

        const promocodeData: Promocode = {
          id: response.data._id || response.data.id,
          code: response.data.code,
          discount: response.data.discount,
          validUntil: response.data.validUntil,
          maxRedemptions: response.data.maxRedemptions || 'Unlimited',
          // usageCount: response.data.usageCount || 0,
          usageCount: response.data.timesRedeemed || 0,
          isActive: response.data.isActive
        };

        setSelectedPromocode(promocodeData);
        setShowEditOffcanvas(true);
      }
    } catch (error: any) {
      console.error("Error fetching promocode details:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load promocode details. Please try again.";

      toast.error(message, {
        position: "top-right"
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Load promocodes on component mount and when page changes
  useEffect(() => {
    fetchPromocodes(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage]);

  // Filter promocodes based on search and status (client-side filtering)
  useEffect(() => {
    let filtered = [...promocodes];

    if (searchTerm) {
      filtered = filtered.filter(promocode =>
        promocode.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(promocode =>
        (statusFilter === 'active' && promocode.isActive) ||
        (statusFilter === 'inactive' && !promocode.isActive)
      );
    }

    setFilteredPromocodes(filtered);
  }, [searchTerm, statusFilter, promocodes]);

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // Handle adding new promocode
  const handleAddPromocode = async (newPromocodeData: any) => {
    // Add new promocode to the top of current lists immediately
    setPromocodes(prevPromocodes => [newPromocodeData, ...prevPromocodes]);
    setFilteredPromocodes(prevFiltered => [newPromocodeData, ...prevFiltered]);

    // Update total count
    setTotalPromocodes(prev => prev + 1);

    // Then refresh from API with current search and filter parameters
    let activeFilter: boolean | undefined;
    if (statusFilter === 'active') {
      activeFilter = true;
    } else if (statusFilter === 'inactive') {
      activeFilter = false;
    }

    await fetchPromocodes(1, itemsPerPage, searchTerm, activeFilter);
    setCurrentPage(1);
  };

  useEffect(() => {
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchDebounce]);

  // Handle updating promocode
  const handleUpdatePromocode = (updatedPromocode: Promocode) => {
    setPromocodes(prevPromocodes =>
      prevPromocodes.map(promo =>
        promo.id === updatedPromocode.id ? updatedPromocode : promo
      )
    );
    setFilteredPromocodes(prevFiltered =>
      prevFiltered.map(promo =>
        promo.id === updatedPromocode.id ? updatedPromocode : promo
      )
    );
  };

  // Handle edit click - fetch full promocode data
  const handleEditClick = (promocode: Promocode) => {
    fetchSinglePromocode(promocode.id.toString());
  };

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Refresh promocodes list
  const handleRefresh = () => {
    fetchPromocodes(currentPage, itemsPerPage);
  };



  const updatePromocodeStatus = async (promocodeId: string, isActive: boolean) => {
    // console.log("Updating promocode status:", promocodeId, isActive);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');


      const userId = localStorage.getItem('authUser')

      if (!userId) {
        toast.error("User ID is missing. Cannot update promocode status.");
        return false;
      }

      const updateUrl = `${AppConstants.ADMIN_GET_PROMOCODES_API}/${promocodeId}`;

      const response = await axios.patch(
        updateUrl,
        {
          isActive: isActive,
          updatedBy: userId
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        toast.success(`Promocode ${isActive ? 'activated' : 'deactivated'} successfully!`, {
          position: "top-right"
        });
        return true;
      }
    } catch (error: any) {
      console.error("Error updating promocode status:", error);
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Failed to update promocode status. Please try again.";
      toast.error(message, {
        position: "top-right"
      });
      return false;
    }
  };

  // 3. Add this new handler function after your existing handleEditClick function
  const handleStatusToggle = async (promocode: Promocode) => {
    const newStatus = !promocode.isActive;

    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You are about to ${newStatus ? 'activate' : 'deactivate'} this promocode.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#28a745' : '#28a745',
      cancelButtonColor: '#4D5E80',
      confirmButtonText: `Yes, ${newStatus ? 'Activate' : 'Deactivate'} it!`
    });

    if (!result.isConfirmed) return;

    // Optimistically update UI
    setPromocodes(prev =>
      prev.map(promo =>
        promo.id === promocode.id ? { ...promo, isActive: newStatus } : promo
      )
    );
    setFilteredPromocodes(prev =>
      prev.map(promo =>
        promo.id === promocode.id ? { ...promo, isActive: newStatus } : promo
      )
    );

    const success = await updatePromocodeStatus(promocode.id.toString(), newStatus);

    if (!success) {
      // Revert back if API failed
      setPromocodes(prev =>
        prev.map(promo =>
          promo.id === promocode.id ? { ...promo, isActive: promocode.isActive } : promo
        )
      );
      setFilteredPromocodes(prev =>
        prev.map(promo =>
          promo.id === promocode.id ? { ...promo, isActive: promocode.isActive } : promo
        )
      );
    }
  };


  return (
    <Container fluid className="p-0 promocode-management mb-5">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <Card className="filter-block p-1 mb-3 rounded shadow-sm border-0">
        <CardBody>
          <Row className="justify-content-between">
            <Col lg={4} md={12} className="mb-3 mb-lg-0 filters">
              <InputGroup>
                <Form.Control
                  placeholder="Search by promocode"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />

              </InputGroup>
            </Col>
            <Col lg={8} md={12} className="filters">
              <div className="d-flex justify-content-end search-create">
                <div className="d-flex align-items-center me-0 me-md-3">
                  <span className="me-2" style={{ minWidth: '60px' }}>Filter by</span>
                  <Form.Select
                    style={{ width: 'auto', minWidth: '230px' }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </div>
                {/* <Button
                  variant="outline-info"
                  onClick={handleRefresh}
                  className="me-2"
                  disabled={loading}
                >
                  Refresh
                </Button> */}
                <Button
                  variant="success"
                  onClick={() => setShowCreateOffcanvas(true)}
                  className="text-white gilroy-semibold px-3 py-2"
                >
                  <MdAdd /> Create Promocode
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {loading ? (
        <Loader text="Loading promocodes..." />
      ) : (
        <>
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th>Promocode</th>
                  <th>Discount</th>
                  <th>Valid Until</th>
                  <th>Usage / Limit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPromocodes.length > 0 ? (
                  filteredPromocodes.map((promocode) => (
                    <tr key={promocode.id} style={{ whiteSpace: "nowrap" }}>
                      <td className="gilroy-semibold text-info">{promocode.code}</td>
                      <td className="text-info">{promocode.discount}% OFF</td>
                      <td className="text-info">{promocode.validUntil}</td>
                      <td className="text-info">
                        {promocode.maxRedemptions === "Unlimited"
                          ? `Unlimited (${promocode.usageCount} used)`
                          : `${promocode.usageCount}/${promocode.maxRedemptions}`
                        }
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          id={`switch-${promocode.id}`}
                          checked={promocode.isActive}
                          onChange={() => handleStatusToggle(promocode)}
                          label=""
                          className="d-inline-block"
                        />
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      {searchTerm || statusFilter !== 'all'
                        ? `No promocodes found matching your criteria`
                        : `No promocodes available`
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2 pagination-block">
            <div>
              Showing: {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalPromocodes)} of {totalPromocodes}
            </div>
            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                />
                {paginationItems}
                <Pagination.Next
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || loading}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                />
              </Pagination>
            )}
          </div>
        </>
      )}

      <CreatePromocodeOffcanvas
        show={showCreateOffcanvas}
        handleClose={() => setShowCreateOffcanvas(false)}
        addPromocode={handleAddPromocode}
      />

      {/* <EditPromocodeOffcanvas
        show={showEditOffcanvas}
        handleClose={() => setShowEditOffcanvas(false)}
        promocode={selectedPromocode}
        updatePromocode={handleUpdatePromocode}
      /> */}

      {editLoading && (
        <div className="position-fixed top-50 start-50 translate-middle" style={{ zIndex: 9999 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading promocode details...</span>
          </div>
        </div>
      )}
    </Container>
  );
};

export default PromocodeManagement;